import React, { useState, useEffect } from 'react';
import { getHealthUrl, getApiBaseUrl } from '../../utils/api-client';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Chip,
  Button,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Define health status type
type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

// Define service health detail type from gateway
interface ServiceHealthDetail {
  status: HealthStatus;
  lastChecked?: string;
  version?: string;
  components?: any;
}

// Define gateway health response type
interface GatewayHealthResponse {
  status: HealthStatus;
  timestamp: string;
  version: string;
  environment: string;
  uptime: number;
  services: Record<string, ServiceHealthDetail>;
}

// Define service health data type for display
interface ServiceHealth {
  name: string;
  status: HealthStatus;
  version: string;
  timestamp: string;
}

const StatusChip: React.FC<{ status: HealthStatus }> = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'healthy':
        return {
          color: 'success' as const,
          icon: <CheckCircleIcon />,
          label: 'Operational',
        };
      case 'degraded':
        return {
          color: 'warning' as const,
          icon: <WarningIcon />,
          label: 'Degraded',
        };
      case 'unhealthy':
      case 'unknown':
        return {
          color: 'error' as const,
          icon: <ErrorIcon />,
          label: 'Outage',
        };
      default:
        return {
          color: 'error' as const,
          icon: <ErrorIcon />,
          label: 'Unknown',
        };
    }
  };

  const { color, icon, label } = getStatusConfig();

  return (
    <Chip
      icon={icon}
      label={label}
      color={color}
      size="small"
      sx={{ fontWeight: 600 }}
    />
  );
};

const ServiceCard: React.FC<{ service: ServiceHealth }> = ({ service }) => {
  const theme = useTheme();

  const getStatusColor = (status: HealthStatus) => {
    switch (status) {
      case 'healthy':
        return theme.palette.success.main;
      case 'degraded':
        return theme.palette.warning.main;
      case 'unhealthy':
      case 'unknown':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor: getStatusColor(service.status),
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, mb: 1.5 }}>
          {service.name}
        </Typography>
        <Box mb={2}>
          <StatusChip status={service.status} />
        </Box>
        {service.version && service.version !== 'unknown' && (
          <Typography variant="caption" color="text.secondary" display="block">
            Version: {service.version}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} mt={0.5}>
          <AccessTimeIcon sx={{ fontSize: 14 }} />
          {new Date(service.timestamp).toLocaleTimeString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

const SystemStatus: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [refreshInterval] = useState<number>(30000); // 30 seconds

  const fetchHealthData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use the lightweight API client utility for environment-aware URLs
      const healthUrl = getHealthUrl();

      console.log('[SystemStatus] Fetching health from:', healthUrl);
      console.log('[SystemStatus] Base API URL:', getApiBaseUrl());

      const res = await fetch(healthUrl);
      const response = await res.json();

      console.log('[SystemStatus] Response status:', res.status);
      console.log('[SystemStatus] Response data:', response);

      const healthData: GatewayHealthResponse = response.data || response;

      if (!healthData) {
        throw new Error('No health data received from gateway');
      }

      // Transform gateway response to ServiceHealth objects
      const servicesData: ServiceHealth[] = [];

      // Add Gateway itself
      servicesData.push({
        name: 'API Gateway',
        status: healthData.status,
        version: healthData.version,
        timestamp: healthData.timestamp,
      });

      // Add individual services from gateway's services check
      if (healthData.services) {
        const serviceDisplayNames: Record<string, string> = {
          user: 'User Service',
          dal: 'Data Layer',
          organization: 'Organization Service',
          analytics: 'Analytics Service',
          content: 'Content Service',
          rbac: 'Access Control',
          subscription: 'Subscription Service',
          communication: 'Communication Service',
        };

        Object.entries(healthData.services).forEach(([serviceName, serviceData]) => {
          const displayName =
            serviceDisplayNames[serviceName] ||
            serviceName.charAt(0).toUpperCase() + serviceName.slice(1).replace('-', ' ');

          servicesData.push({
            name: displayName,
            status: serviceData.status,
            version: serviceData.version || 'unknown',
            timestamp: serviceData.lastChecked || healthData.timestamp,
          });
        });
      }

      setServices(servicesData);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Error fetching health data:', err);
      setError('Unable to retrieve system status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();

    // Set up refresh interval
    const intervalId = setInterval(fetchHealthData, refreshInterval);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [refreshInterval]);

  // Calculate overall system status
  const getOverallStatus = (): HealthStatus => {
    if (services.length === 0) return 'unknown';
    if (services.some((s) => s.status === 'unhealthy')) return 'unhealthy';
    if (services.some((s) => s.status === 'degraded' || s.status === 'unknown')) return 'degraded';
    return 'healthy';
  };

  const overallStatus = getOverallStatus();

  const getOverallStatusMessage = () => {
    switch (overallStatus) {
      case 'healthy':
        return 'All systems operational';
      case 'degraded':
        return 'Experiencing minor issues';
      case 'unhealthy':
        return 'Service disruption detected';
      default:
        return 'Status unknown';
    }
  };

  const getOverallStatusColor = () => {
    switch (overallStatus) {
      case 'healthy':
        return theme.palette.success.main;
      case 'degraded':
        return theme.palette.warning.main;
      case 'unhealthy':
      case 'unknown':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  if (loading && services.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            System Status
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Real-time status and monitoring of our services
          </Typography>
        </Box>

        {/* Overall Status Card */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 4,
            background: `linear-gradient(135deg, ${alpha(getOverallStatusColor(), 0.1)} 0%, ${alpha(
              getOverallStatusColor(),
              0.05
            )} 100%)`,
            borderTop: 4,
            borderColor: getOverallStatusColor(),
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                {overallStatus === 'healthy' && <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />}
                {overallStatus === 'degraded' && <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />}
                {(overallStatus === 'unhealthy' || overallStatus === 'unknown') && (
                  <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
                )}
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                    {getOverallStatusMessage()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5} mt={0.5}>
                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                    Last updated: {lastUpdated.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchHealthData}
                disabled={loading}
                sx={{ minWidth: 140 }}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Box
              mt={3}
              p={2}
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              }}
            >
              <Typography color="error.main" display="flex" alignItems="center" gap={1}>
                <ErrorIcon />
                {error}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Service Dependency Visualization */}
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Service Architecture
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            {/* API Gateway Layer */}
            <Box textAlign="center" sx={{ maxWidth: 300, width: '100%' }}>
              {services.find((s) => s.name === 'API Gateway') && (
                <ServiceCard service={services.find((s) => s.name === 'API Gateway')!} />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Primary entry point for all requests
              </Typography>
            </Box>

            {/* Connection Indicator */}
            <Box
              sx={{
                width: 2,
                height: 40,
                bgcolor: 'divider',
              }}
            />

            {/* Business Services Layer */}
            <Box textAlign="center" width="100%">
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Business Services
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                {services
                  .filter((service) => service.name !== 'API Gateway' && service.name !== 'Data Layer')
                  .map((service) => (
                    <Grid item xs={12} sm={6} md={3} key={service.name}>
                      <ServiceCard service={service} />
                    </Grid>
                  ))}
              </Grid>
            </Box>

            {/* Connection Indicator to DAL */}
            {services.find((s) => s.name === 'Data Layer') && (
              <>
                <Box
                  sx={{
                    width: 2,
                    height: 40,
                    bgcolor: 'divider',
                  }}
                />

                {/* Data Access Layer */}
                <Box textAlign="center">
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Data Access Layer
                  </Typography>
                  <Box display="flex" justifyContent="center" mt={1}>
                    <ServiceCard service={services.find((s) => s.name === 'Data Layer')!} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Centralized database operations and data management
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Paper>

        {/* Auto-refresh Notice */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" color="text.secondary">
            This page automatically refreshes every 30 seconds
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default SystemStatus;
